import React, { useEffect, useState } from "react";
import { Shield, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CHALLENGE_EVENT, resolveTwofaChallenge } from "../../lib/twofaChallenge";
import api from "../../lib/apiClient";

// TwoFactorChallengeDialog — sits at the root of the app, listens for the
// `globaleyes:twofa-challenge` event, and pops a modal asking the user
// for a 6-digit code (or a backup code). On success, dispatches the result
// back to whoever fired the event via `globaleyes:twofa-challenge:result`.
// On cancel, dispatches `{ok: false}` and the original request rejects.
const TwoFactorChallengeDialog = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = () => { setOpen(true); setCode(""); setBackup(""); setUseBackup(false); setError(""); };
    window.addEventListener(CHALLENGE_EVENT, handler);
    return () => window.removeEventListener(CHALLENGE_EVENT, handler);
  }, []);

  if (!open) return null;

  const close = (cancelled = true) => {
    setOpen(false);
    if (cancelled) resolveTwofaChallenge(false, null, 0, "User cancelled 2FA challenge");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const token = (() => {
      try { return window.localStorage.getItem("globaleyes_token") || ""; } catch (_) { return ""; }
    })();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setSubmitting(true);
    try {
      // Raw axios here (not the apiClient) to avoid recursion if THIS call
      // itself returned a 2FA challenge somehow.
      const body = useBackup ? { backup_code: backup.trim().toUpperCase() } : { code: code.trim() };
      const { data } = await api.post(`/auth/2fa/verify`, body);
      setOpen(false);
      resolveTwofaChallenge(true, data.stepup_token, data.expires_in_seconds, null);
    } catch (err) {
      setError(err?.response?.data?.detail || "That code is incorrect. Try again.");
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center px-4"
      data-testid="twofa-challenge-overlay"
      onClick={() => close(true)}
    >
      <div
        className="bg-gray-900 border border-emerald-500/30 rounded-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="twofa-challenge-dialog"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-400" />
            <h3 className="text-white font-semibold">Confirm with 2FA</h3>
          </div>
          <button onClick={() => close(true)} className="text-gray-500 hover:text-white" aria-label="Cancel" data-testid="twofa-challenge-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          This action needs a second factor. Enter the 6-digit code from your authenticator app.
        </p>
        <form onSubmit={submit} className="space-y-3">
          {!useBackup ? (
            <div>
              <Label className="text-gray-300 text-sm">Authenticator code</Label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="bg-gray-800 border-gray-700 text-white text-center tracking-[0.5em] text-lg font-mono mt-1"
                data-testid="twofa-challenge-code"
              />
            </div>
          ) : (
            <div>
              <Label className="text-gray-300 text-sm">Backup code</Label>
              <Input
                type="text"
                value={backup}
                onChange={(e) => setBackup(e.target.value.toUpperCase().slice(0, 11))}
                placeholder="ABCDE-FGHIJ"
                autoFocus
                className="bg-gray-800 border-gray-700 text-white text-center tracking-[0.3em] text-base font-mono mt-1"
                data-testid="twofa-challenge-backup"
              />
            </div>
          )}
          {error && <p className="text-red-400 text-sm" data-testid="twofa-challenge-error">{error}</p>}
          <Button
            type="submit"
            disabled={submitting || (useBackup ? backup.length < 10 : code.length !== 6)}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="twofa-challenge-submit"
          >
            {submitting ? "Verifying..." : "Confirm"}
          </Button>
          <button
            type="button"
            onClick={() => { setUseBackup((v) => !v); setError(""); }}
            className="block w-full text-center text-xs text-gray-500 hover:text-emerald-400 transition-colors"
            data-testid="twofa-challenge-toggle"
          >
            {useBackup ? "← Use authenticator code" : "Use a backup code instead"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorChallengeDialog;
